import React, { useState, useEffect } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { evalApi } from '@/services/evalApi';
import { evalFunctionApi } from '@/services/evalFunctionApi';
import { ObjectivesEditor, Objective } from './ObjectivesEditor';
import { normalizeWeights } from './hooks/useNormalizedWeights';

interface Variant {
  name: string;
  model?: string | { name: string };
  temperature?: number;
  agent_version?: number;
}

interface CopyAsOptimizableDialogProps {
  open: boolean;
  onClose: () => void;
  evalId: string;
  evalName: string;
  evalData?: {
    judge?: { criteria?: Array<{ name: string; weight?: number }> };
    metrics?: string[];
    variants?: Variant[];
  };
  onSuccess?: (newEvalId: string) => void;
}

interface OptimizableField {
  field_path: string;
  field_type: 'text' | 'numeric' | 'enum' | 'list';
  mutation_strategy: 'llm_rewrite' | 'interpolate' | 'perturb' | 'add_remove';
  preserve_parameters: boolean;
}

const DEFAULT_FIELDS: OptimizableField[] = [
  {
    field_path: 'prompt',
    field_type: 'text',
    mutation_strategy: 'llm_rewrite',
    preserve_parameters: true,
  },
];

const FIELD_OPTIONS = [
  { path: 'prompt', label: 'System Prompt', type: 'text' as const },
  { path: 'user_prompt', label: 'User Prompt (body)', type: 'text' as const },
  { path: 'model.temperature', label: 'Temperature', type: 'numeric' as const },
  { path: 'reasoning.effort', label: 'Thinking Level', type: 'enum' as const },
  { path: 'tools', label: 'Tools', type: 'list' as const },
];

export function CopyAsOptimizableDialog({
  open,
  onClose,
  evalId,
  evalName,
  evalData,
  onSuccess,
}: CopyAsOptimizableDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [optimizableFields, setOptimizableFields] = useState<OptimizableField[]>(DEFAULT_FIELDS);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [maxIterations, setMaxIterations] = useState('10');
  const [trialsPerIteration, setTrialsPerIteration] = useState('3');
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [directives, setDirectives] = useState<string[]>([]);
  const [newDirective, setNewDirective] = useState('');

  // Selected field paths for checkboxes
  const [selectedFieldPaths, setSelectedFieldPaths] = useState<Set<string>>(new Set(['prompt']));

  // Available eval functions for objectives (id + name for display)
  const [availableEvalFunctions, setAvailableEvalFunctions] = useState<Array<{id: string; name: string}>>([]);

  // Extract available criteria from eval data
  const availableCriteria = evalData?.judge?.criteria?.map((c) => c.name) || [];
  const availableMetrics = evalData?.metrics || ['latency_ms', 'total_tokens', 'tool_call_count'];

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      setName(`${evalName} (Optimized)`);
      setError(null);
      setSelectedFieldPaths(new Set(['prompt']));
      setOptimizableFields(DEFAULT_FIELDS);
      setMaxIterations('10');
      setTrialsPerIteration('3');
      setGoalDescription('');

      // Initialize selected variant (default to first variant if multiple exist)
      if (evalData?.variants && evalData.variants.length > 0) {
        setSelectedVariant(evalData.variants[0].name);
      } else {
        setSelectedVariant(null);
      }

      // Fetch available eval functions
      evalFunctionApi.list().then((funcs) => {
        setAvailableEvalFunctions(funcs.map(f => ({ id: f.id, name: f.name })));
      }).catch(() => {
        // Silently fail - eval functions are optional
        setAvailableEvalFunctions([]);
      });

      // Auto-populate objectives from eval data
      const autoObjectives: Objective[] = [];

      // Add judge_score objective if there's a judge
      if (evalData?.judge) {
        autoObjectives.push({
          name: 'quality',
          source: 'judge_score',
          direction: 'maximize',
          weight: 0.6,
        });
      }

      // Add metric objectives
      if (evalData?.metrics) {
        if (evalData.metrics.includes('latency_ms')) {
          autoObjectives.push({
            name: 'latency',
            source: 'metric',
            source_key: 'latency_ms',
            direction: 'minimize',
            weight: 0.2,
          });
        }
        if (evalData.metrics.includes('total_tokens')) {
          autoObjectives.push({
            name: 'cost',
            source: 'metric',
            source_key: 'total_tokens',
            direction: 'minimize',
            weight: 0.2,
          });
        }
      }

      // Default objective if none auto-populated
      if (autoObjectives.length === 0) {
        autoObjectives.push({
          name: 'quality',
          source: 'judge_score',
          direction: 'maximize',
          weight: 1.0,
        });
      }

      // Normalize weights to ensure they sum to 100%
      setObjectives(normalizeWeights(autoObjectives));

      // Reset directives
      setDirectives([]);
      setNewDirective('');
    }
  }, [open, evalName, evalData]);

  // Update optimizableFields when checkboxes change
  useEffect(() => {
    const fields: OptimizableField[] = [];
    selectedFieldPaths.forEach((path) => {
      const fieldOption = FIELD_OPTIONS.find((f) => f.path === path);
      if (fieldOption) {
        // Determine mutation strategy based on field type
        let mutationStrategy: 'llm_rewrite' | 'interpolate' | 'perturb' | 'add_remove' = 'llm_rewrite';
        if (fieldOption.type === 'numeric') {
          mutationStrategy = 'perturb';
        } else if (fieldOption.type === 'list') {
          mutationStrategy = 'add_remove';
        }
        // text and enum both use llm_rewrite

        fields.push({
          field_path: path,
          field_type: fieldOption.type,
          mutation_strategy: mutationStrategy,
          preserve_parameters: fieldOption.type === 'text',
        });
      }
    });
    setOptimizableFields(fields);
  }, [selectedFieldPaths]);

  // Helper to display variant details
  const getVariantModelDisplay = (variant: Variant): string => {
    if (!variant.model) return 'default model';
    if (typeof variant.model === 'string') return variant.model;
    return variant.model.name || 'unknown';
  };

  const getVariantDetails = (variant: Variant): string => {
    const parts = [getVariantModelDisplay(variant)];
    if (variant.temperature !== undefined) {
      parts.push(`temp=${variant.temperature}`);
    }
    return parts.join(', ');
  };

  const handleFieldToggle = (path: string, checked: boolean) => {
    setSelectedFieldPaths((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(path);
      } else {
        next.delete(path);
      }
      return next;
    });
  };

  const handleAddDirective = () => {
    if (!newDirective.trim()) return;
    setDirectives([...directives, newDirective.trim()]);
    setNewDirective('');
  };

  const handleRemoveDirective = (index: number) => {
    setDirectives(directives.filter((_, i) => i !== index));
  };

  const handleDirectiveKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDirective();
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (optimizableFields.length === 0) {
      setError('At least one field to optimize is required');
      return;
    }
    if (objectives.length === 0) {
      setError('At least one objective is required');
      return;
    }

    // Ensure objectives have names
    const validObjectives = objectives.filter((o) => o.name.trim());
    if (validObjectives.length === 0) {
      setError('At least one objective must have a name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await evalApi.copyAsOptimizable(evalId, {
        name: name.trim(),
        optimizable_fields: optimizableFields,
        objectives: validObjectives,
        limits: {
          max_iterations: parseInt(maxIterations, 10),
          trials_per_iteration: parseInt(trialsPerIteration, 10),
        },
        goal_description: goalDescription.trim() || undefined,
        // Include selected variant if there are multiple variants
        selected_variant: evalData?.variants && evalData.variants.length > 1
          ? selectedVariant
          : undefined,
        // Include initial directives
        directives: directives.length > 0 ? directives : undefined,
      });

      if (onSuccess) {
        onSuccess(result.id);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create optimizable eval');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Copy as Optimizable Eval
          </DialogTitle>
          <DialogDescription>
            Create a copy of this eval with optimization settings configured.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Optimizable Eval"
            />
          </div>

          {/* Goal Description */}
          <div className="space-y-2">
            <Label htmlFor="goal">Goal (optional)</Label>
            <Textarea
              id="goal"
              value={goalDescription}
              onChange={(e) => setGoalDescription(e.target.value)}
              placeholder="e.g., Improve response quality while reducing token usage"
              rows={2}
            />
          </div>

          {/* Directives */}
          <div className="space-y-2">
            <Label>Optimizer Directives (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Standing instructions the optimizer must follow in all iterations.
            </p>
            {directives.length > 0 && (
              <div className="space-y-1">
                {directives.map((directive, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-muted/50 rounded-md text-sm"
                  >
                    <span className="flex-1">{directive}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleRemoveDirective(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={newDirective}
                onChange={(e) => setNewDirective(e.target.value)}
                onKeyDown={handleDirectiveKeyDown}
                placeholder="e.g., Keep the prompt under 500 words"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddDirective}
                disabled={!newDirective.trim()}
              >
                Add
              </Button>
            </div>
          </div>

          {/* Variant Selection - only show if multiple variants exist */}
          {evalData?.variants && evalData.variants.length > 1 && (
            <div className="space-y-2">
              <Label htmlFor="variant">Base Variant</Label>
              <Select
                value={selectedVariant || undefined}
                onValueChange={setSelectedVariant}
              >
                <SelectTrigger id="variant">
                  <SelectValue placeholder="Select a variant" />
                </SelectTrigger>
                <SelectContent>
                  {evalData.variants.map((variant) => (
                    <SelectItem key={variant.name} value={variant.name}>
                      {variant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedVariant && (
                <p className="text-sm text-muted-foreground">
                  {getVariantDetails(
                    evalData.variants.find((v) => v.name === selectedVariant) || evalData.variants[0]
                  )}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                The new optimizable eval will use this variant as the baseline. Other variants will not be copied.
              </p>
            </div>
          )}

          {/* Objectives - using new component */}
          <ObjectivesEditor
            objectives={objectives}
            onChange={setObjectives}
            availableCriteria={availableCriteria}
            availableMetrics={availableMetrics}
            availableEvalFunctions={availableEvalFunctions}
            showPresets={true}
            minObjectives={1}
          />

          {/* Fields to Optimize */}
          <div className="space-y-3">
            <Label>Fields to Optimize</Label>
            <div className="space-y-2">
              {FIELD_OPTIONS.map((field) => (
                <div key={field.path} className="flex items-center gap-2">
                  <Checkbox
                    id={`field-${field.path}`}
                    checked={selectedFieldPaths.has(field.path)}
                    onCheckedChange={(checked) =>
                      handleFieldToggle(field.path, checked === true)
                    }
                  />
                  <Label
                    htmlFor={`field-${field.path}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {field.label}{' '}
                    <span className="text-muted-foreground">({field.type})</span>
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="maxIterations">Max Iterations</Label>
              <Input
                id="maxIterations"
                type="number"
                value={maxIterations}
                onChange={(e) => setMaxIterations(e.target.value)}
                min="1"
                max="100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trialsPerIteration">Trials per Iteration</Label>
              <Input
                id="trialsPerIteration"
                type="number"
                value={trialsPerIteration}
                onChange={(e) => setTrialsPerIteration(e.target.value)}
                min="1"
                max="20"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Create & Open
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
